import { cloneValue } from './configPaths'

/**
 * Per-template snapshots of one ad, so switching Split Slider → Story → Reel → Split Slider
 * brings each template back exactly as it was left (texts, images, colours, slides, style).
 *
 * Kept in memory for the session (pending uploads are File objects and data URLs) and, once
 * the ad has an id, in localStorage without data URLs. Keyed by template family; a snapshot
 * is only restored into the same release it was taken from.
 */

const PREFIX = 'studio-editor-v2:template-snapshots:'
const ASSET_KEYS = new Set(['sourceAssetId', 'renditionAssetId'])

function storage() {
  try { return globalThis.localStorage || null } catch { return null }
}

const persistable = (urls) => Object.fromEntries(Object.entries(urls || {})
  .filter(([, url]) => typeof url === 'string' && /^(https?:\/\/|\/)/.test(url)))

export function loadTemplateSnapshots(adId) {
  if (!adId) return {}
  try {
    const parsed = JSON.parse(storage()?.getItem(PREFIX + adId) || 'null')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function persistTemplateSnapshots(adId, snapshots) {
  const store = storage()
  if (!adId || !store) return
  const slim = Object.fromEntries(Object.entries(snapshots || {}).map(([family, snapshot]) => [family, {
    releaseKey: snapshot.releaseKey,
    takenAt: snapshot.takenAt,
    configuration: snapshot.configuration,
    assetUrls: persistable(snapshot.assetUrls),
    assetSourceUrls: persistable(snapshot.assetSourceUrls),
    assetSrcSets: snapshot.assetSrcSets || {},
  }]))
  try {
    if (Object.keys(slim).length) store.setItem(PREFIX + adId, JSON.stringify(slim))
    else store.removeItem(PREFIX + adId)
  } catch { /* Quota or private mode: the in-memory snapshots still work for this session. */ }
}

export function takeSnapshot(template, configuration, assetUrls, assetSourceUrls, assetSrcSets) {
  return {
    releaseKey: template?.manifest?.releaseKey || template?.manifest?.id || null,
    takenAt: new Date().toISOString(),
    configuration: cloneValue(configuration),
    assetUrls: { ...(assetUrls || {}) },
    assetSourceUrls: { ...(assetSourceUrls || {}) },
    assetSrcSets: { ...(assetSrcSets || {}) },
  }
}

function walk(value, visit) {
  if (Array.isArray(value)) return value.map((entry) => walk(entry, visit))
  if (value && typeof value === 'object') {
    const next = {}
    Object.entries(value).forEach(([key, entry]) => { next[key] = walk(entry, visit) })
    return visit(next)
  }
  return value
}

/** Every asset id a configuration's media references point at. */
export function referencedAssetIds(configuration) {
  const ids = new Set()
  walk(configuration?.data || {}, (node) => {
    ASSET_KEYS.forEach((key) => { if (typeof node[key] === 'string' && node[key]) ids.add(node[key]) })
    return node
  })
  return ids
}

/**
 * What the server made of the uploads in one save: the pending id sent at a path and the stored
 * id it came back as. Used to point the other templates' snapshots at the stored files.
 */
export function savedAssetMapping(sent, saved) {
  const mapping = {}
  const pair = (a, b) => {
    if (Array.isArray(a) && Array.isArray(b)) { a.forEach((entry, index) => pair(entry, b[index])); return }
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return
    // Sources first: a fit-mode image sends its pending source id as the rendition too, and the
    // stored rendition must not take the source's place.
    ;['sourceAssetId', 'renditionAssetId'].forEach((key) => {
      if (typeof a[key] === 'string' && a[key].startsWith('pending-') && typeof b[key] === 'string'
          && b[key] !== a[key] && !(key === 'renditionAssetId' && mapping[a[key]])) {
        mapping[a[key]] = b[key]
      }
    })
    Object.keys(a).forEach((key) => pair(a[key], b[key]))
  }
  pair(sent?.data, saved?.data)
  return mapping
}

export function remapSnapshot(snapshot, mapping, savedConfiguration) {
  if (!Object.keys(mapping).length) return snapshot
  const configuration = walk(snapshot.configuration, (node) => {
    ASSET_KEYS.forEach((key) => { if (mapping[node[key]]) node[key] = mapping[node[key]] })
    return node
  })
  // The stored records travel with the snapshot so its next save can reuse them by id.
  configuration.assets = { ...(configuration.assets || {}), ...(savedConfiguration?.assets || {}) }
  return { ...snapshot, configuration }
}

/**
 * Media whose file is gone (a pending upload lost with a reload) is emptied, so the restored
 * template shows an empty image field instead of failing on save.
 */
export function withoutLostMedia(configuration, isAvailable) {
  return walk(configuration, (node) => {
    if (typeof node.sourceAssetId === 'string' && node.sourceAssetId && !isAvailable(node.sourceAssetId)) {
      node.sourceAssetId = null
      if ('renditionAssetId' in node) node.renditionAssetId = null
      if ('crop' in node) node.crop = null
    }
    return node
  })
}
